import {TlgchEntryScope} from '@tlg-contenthub/live-preview/react';
import React, {ReactNode} from 'react';

import useRequestClass from 'coreHooks/useRequestClass';
import {isSyntheticEntryId} from 'coreUtils/livePreview';

interface LivePreviewEntryProps {
	entryId?: string | null;
	/** Content type shown in the "Edit" label when the CMS does not tell it. */
	type?: string;
	/** Field "Edit" opens. */
	fieldId?: string;
	children: ReactNode;
}

/**
 * Whether an entry gets live preview tags: a CMS entry, in preview.
 * @param isPreview Whether the request is a live or CMS preview
 * @param entryId Value of `sys.id`
 * @returns {boolean}
 */
const isTaggable = (isPreview: boolean, entryId?: string | null): entryId is string =>
	isPreview && !!entryId && !isSyntheticEntryId(entryId);

/**
 * Tags `children` as a CMS entry in live preview, for entries the components builder does not build
 * (it tags its own elements). The tag lives in React's fiber tree, where the SDK reads it, and adds no DOM:
 * the entry's element is the first DOM node of `children`. Outside preview only `children` are rendered.
 */
const LivePreviewEntry = ({entryId, type, fieldId, children}: LivePreviewEntryProps): React.ReactElement => {
	const {isLivePreview, isCmsPreview} = useRequestClass();

	if (!isTaggable(isLivePreview || isCmsPreview, entryId)) {
		return <>{children}</>;
	}

	return <TlgchEntryScope id={entryId} type={type} fieldId={fieldId}>{children}</TlgchEntryScope>;
};

export default LivePreviewEntry;
